import React from "react";
import { toast } from "react-hot-toast";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, FormGroup, Label, Input } from "reactstrap";

/**
 * Modal para aplicar, modificar o eliminar descuentos manuales.
 * Componente puro: recibe estado y handlers como props.
 */
export function ModalDescuento({
  showDiscountModal,
  setShowDiscountModal,
  discountMethod,
  setDiscountMethod,
  finalAmountStr,
  setFinalAmountStr,
  manualDiscountAmountStr,
  setManualDiscountAmountStr,
  discountPercentageStr,
  setDiscountPercentageStr,
  finalAmount,
  setFinalAmount,
  manualDiscountAmount,
  setManualDiscountAmount,
  discountPercentage,
  setDiscountPercentage,
  setDiscountType,
  setOriginalAmount,
  setForceRecalc,
  selectedMoneda,
  inversionFinalTotal,
  formatAmount,
  validateDiscount,
  isEditMode,
  rol,
  customModalClassName,
}) {
  return (
    <Modal isOpen={showDiscountModal} toggle={() => setShowDiscountModal(false)} className={customModalClassName}>
      <ModalHeader toggle={() => setShowDiscountModal(false)}>Aplicar Descuento Manual</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label className="mb-2">¿Qué descuento deseas realizar?</Label>
          <div className="d-flex flex-column gap-2">
            <FormGroup check>
              <Input
                type="radio"
                name="discountMethod"
                value="finalAmount"
                checked={discountMethod === "finalAmount"}
                onChange={() => {
                  setDiscountMethod("finalAmount");
                  setFinalAmountStr("");
                  setFinalAmount(0);
                }}
              />
              <Label check>Definir un monto objetivo</Label>
            </FormGroup>
            <FormGroup check>
              <Input
                type="radio"
                name="discountMethod"
                value="discountAmount"
                checked={discountMethod === "discountAmount"}
                onChange={() => {
                  setDiscountMethod("discountAmount");
                  setManualDiscountAmountStr("");
                  setManualDiscountAmount(0);
                }}
              />
              <Label check>Descontar un monto</Label>
            </FormGroup>
            <FormGroup check>
              <Input
                type="radio"
                name="discountMethod"
                value="percentage"
                checked={discountMethod === "percentage"}
                onChange={() => {
                  setDiscountMethod("percentage");
                  setDiscountPercentageStr("");
                  setDiscountPercentage(0);
                }}
              />
              <Label check>Descontar un porcentaje</Label>
            </FormGroup>
            {manualDiscountAmount > 0 && (
              <FormGroup check>
                <Input
                  type="radio"
                  name="discountMethod"
                  value="remove"
                  checked={discountMethod === "remove"}
                  onChange={() => setDiscountMethod("remove")}
                />
                <Label check className="text-danger">
                  Eliminar descuento
                </Label>
              </FormGroup>
            )}
          </div>
        </FormGroup>
        {discountMethod !== "remove" && (
          <FormGroup>
            <Label>
              {discountMethod === "finalAmount"
                ? "Ingresa el Monto Final Deseado"
                : discountMethod === "discountAmount"
                  ? "Ingresa el Monto de Descuento"
                  : "Ingresa el Porcentaje de Descuento"}
            </Label>
            <div style={{ position: "relative", display: "inline-block", width: "100%" }}>
              <Input
                type="text"
                value={
                  discountMethod === "finalAmount"
                    ? finalAmountStr
                      ? formatAmount(parseInt(finalAmountStr, 10), selectedMoneda === "$" ? "CLP" : "PEN")
                      : ""
                    : discountMethod === "discountAmount"
                      ? manualDiscountAmountStr
                        ? formatAmount(parseInt(manualDiscountAmountStr, 10), selectedMoneda === "$" ? "CLP" : "PEN")
                        : ""
                      : discountPercentageStr
                }
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, ""); // Solo dígitos
                  if (discountMethod === "percentage") {
                    value = e.target.value.replace(/,/g, ".").replace(/[^\d.]/g, "");
                    const parts = value.split(".");
                    if (parts.length > 2) value = `${parts[0]}.${parts.slice(1).join("")}`;
                    const numValue = parseFloat(value);
                    if (numValue > 100) value = "100";
                    if (parts[1]?.length > 2) value = `${parts[0]}.${parts[1].slice(0, 2)}`;
                  }
                  if (discountMethod === "finalAmount") setFinalAmountStr(value);
                  else if (discountMethod === "discountAmount") setManualDiscountAmountStr(value);
                  else setDiscountPercentageStr(value);
                }}
                onBlur={(e) => {
                  let rawValue = e.target.value.replace(/\D/g, "");
                  if (discountMethod === "percentage") {
                    rawValue = e.target.value.replace(/\.$/, "");
                    const numValue = parseFloat(rawValue) || 0;
                    rawValue = numValue.toFixed(2);
                  } else {
                    rawValue = parseInt(rawValue, 10) || "";
                  }
                  if (discountMethod === "finalAmount") setFinalAmountStr(String(rawValue));
                  else if (discountMethod === "discountAmount") setManualDiscountAmountStr(String(rawValue));
                  else setDiscountPercentageStr(String(rawValue));
                }}
                onFocus={(e) => e.target.select()}
                placeholder={discountMethod === "percentage" ? "Ejemplo: 10.5 (máx 100)" : "Ejemplo: 3.500.000"}
                style={{ paddingRight: discountMethod === "percentage" ? "30px" : "8px" }}
                className="custom-input"
                invalid={discountMethod === "percentage" && parseFloat(discountPercentageStr) > 100}
              />
              {discountMethod === "percentage" && (
                <span
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#999",
                    fontSize: "1rem",
                    pointerEvents: "none",
                  }}
                >
                  %
                </span>
              )}
              {discountMethod === "percentage" && parseFloat(discountPercentageStr) > 100 && (
                <div className="invalid-feedback">El porcentaje no puede exceder 100.</div>
              )}
            </div>
          </FormGroup>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={() => setShowDiscountModal(false)}>
          Cancelar
        </Button>
        <Button
          color="primary"
          onClick={() => {
            let newFinalAmount = 0;
            let newDiscountAmount = 0;
            let newPercentage = 0;
            let newDiscountType = discountMethod; // 'finalAmount' -> 'final', etc.
            if (discountMethod === "finalAmount") {
              newFinalAmount = parseInt(finalAmountStr, 10) || 0;
              newDiscountAmount = inversionFinalTotal - newFinalAmount;
              newPercentage = inversionFinalTotal > 0 ? (newDiscountAmount / inversionFinalTotal) * 100 : 0;
              newDiscountType = "final";
            } else if (discountMethod === "discountAmount") {
              newDiscountAmount = parseInt(manualDiscountAmountStr, 10) || 0;
              newFinalAmount = inversionFinalTotal - newDiscountAmount;
              newPercentage = inversionFinalTotal > 0 ? (newDiscountAmount / inversionFinalTotal) * 100 : 0;
              newDiscountType = "amount";
            } else if (discountMethod === "percentage") {
              newPercentage = parseFloat(discountPercentageStr) || 0;
              if (rol === "comercial") newPercentage = Math.min(newPercentage, 10);
              else if (!["superadmin", "gerencia"].includes(rol)) newPercentage = 0;
              newDiscountAmount = (newPercentage / 100) * inversionFinalTotal;
              newFinalAmount = inversionFinalTotal - newDiscountAmount;
              newDiscountType = "percentage";
            } else if (discountMethod === "remove") {
              // Eliminar descuento
              setDiscountType("none");
              setFinalAmount(0);
              setManualDiscountAmount(0);
              setDiscountPercentage(0);
              setOriginalAmount(0);
              setFinalAmountStr("");
              setManualDiscountAmountStr("");
              setDiscountPercentageStr("");
              setShowDiscountModal(false);
              setForceRecalc((prev) => prev + 1);
              setDiscountMethod("finalAmount");
              toast.success("Descuento eliminado");
              return;
            }
            newFinalAmount = Math.floor(newFinalAmount);
            newDiscountAmount = Math.floor(newDiscountAmount);
            newPercentage = parseFloat(newPercentage.toFixed(2));
            const error = validateDiscount(inversionFinalTotal, newDiscountAmount, newFinalAmount, rol);
            if (error) {
              toast.error(error);
              return;
            }
            setDiscountType(newDiscountType);
            setFinalAmount(newFinalAmount);
            setManualDiscountAmount(newDiscountAmount);
            setDiscountPercentage(newPercentage);
            setOriginalAmount(inversionFinalTotal);
            setFinalAmountStr(newFinalAmount.toString());
            setManualDiscountAmountStr(newDiscountAmount.toString());
            setDiscountPercentageStr(newPercentage.toString());
            setShowDiscountModal(false);
            setForceRecalc((prev) => prev + 1);
            toast("Descuento aplicado localmente. Guarda los cambios con 'Editar'.", {
              icon: "\u2139\uFE0F",
              duration: 5000,
            });
          }}
        >
          Confirmar
        </Button>
        {isEditMode && manualDiscountAmount > 0 && (
          <Button
            color="danger"
            onClick={() => {
              setDiscountType("none");
              setManualDiscountAmount(0);
              setFinalAmount(inversionFinalTotal);
              setDiscountPercentage(0);
              setOriginalAmount(inversionFinalTotal);
              setManualDiscountAmountStr("");
              setFinalAmountStr(inversionFinalTotal.toString());
              setDiscountPercentageStr("0");
              setShowDiscountModal(false);
              setForceRecalc((prev) => prev + 1);
              toast.success("Descuento eliminado");
            }}
          >
            Eliminar Descuento
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}
